import request from './index'
import { encryptPassword } from '@/utils/crypto'

// 生成验证码 URL
export function getCaptchaUrl(length = 4, timestamp = Date.now().toString()) {
  return `/OAuth/ImageCode/${length}/${timestamp}`
}


// 认证相关 API
const authApi = {
  // 生成验证码 URL（改为可接受 timestamp 的版本）
  getCaptchaImageUrl(length = 4, timestamp = Date.now().toString()) {
    const baseURL = '/api'  // 或者从 axios 实例中获取
    return `${baseURL}/OAuth/ImageCode/${length}/${timestamp}?_t=${timestamp}`
  }
  ,

  // 用户登录
  async login(params) {
    if (!params?.account || !params?.password) {
      return Promise.reject(new Error('账号和密码不能为空'))
    }
    const formData = new URLSearchParams()
    formData.append('account', params.account.trim())
    formData.append('password', encryptPassword(params.password))
    formData.append('origin', params.origin || 'password')
    formData.append('grant_type', params.grant_type || 'password')
    if (params.code) formData.append('code', params.code)
    if (params.timestamp) formData.append('timestamp', params.timestamp)
    return request.post('/OAuth/Login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      skipAuth: true,
      timeout: 30000
    })
  },

  // 发送短信验证码 - 根据文档添加
  async sendSmsCode(mobile, scene) {
    if (!mobile || !scene) {
      return Promise.reject(new Error('手机号和场景不能为空'))
    }
    // 验证手机号格式
    if (!/^1[3-9]\d{9}$/.test(mobile)) {
      return Promise.reject(new Error('手机号格式不正确'))
    }
    // 验证场景参数
    const validScenes = ['register', 'login', 'reset_password']
    if (!validScenes.includes(scene)) {
      return Promise.reject(new Error('无效的场景参数'))
    }
    return request.post('/OAuth/sms/send-code', {
      mobile,
      scene
    }, {
      skipAuth: true
    })
  },

  // 用户注册 - 根据文档添加
  async register(params) {
    // 参数验证
    if (!params.mobile || !params.realName || !params.password || !params.verifyCode) {
      return Promise.reject(new Error('请填写完整信息'))
    }
    // 验证手机号格式
    if (!/^1[3-9]\d{9}$/.test(params.mobile)) {
      return Promise.reject(new Error('手机号格式不正确'))
    }
    // 验证验证码为6位数字
    if (!/^\d{6}$/.test(params.verifyCode)) {
      return Promise.reject(new Error('验证码为6位数字'))
    }
    // 验证姓名长度
    if (params.realName.length < 2 || params.realName.length > 50) {
      return Promise.reject(new Error('姓名长度为2-50个字符'))
    }
    // 密码加密
    const encryptedPassword = encryptPassword(params.password)
    // 构建注册参数
    const registerParams = {
      mobile: params.mobile,
      realName: params.realName,
      password: encryptedPassword,
      verifyCode: params.verifyCode
    }
    return request.post('/OAuth/register', registerParams, {
      skipAuth: true
    })
  },

  // 重置密码（手机验证码方式） - 根据文档修改
  async resetPassword(mobile, smsCode) {
    if (!mobile || !smsCode) {
      return Promise.reject(new Error('手机号和验证码不能为空'))
    }
    // 验证手机号格式
    if (!/^1[3-9]\d{9}$/.test(mobile)) {
      return Promise.reject(new Error('手机号格式不正确'))
    }
    // 验证验证码为6位数字
    if (!/^\d{6}$/.test(smsCode)) {
      return Promise.reject(new Error('验证码为6位数字'))
    }
    // 根据文档，重置密码接口是 GET 请求，路径参数
    return request.get(`/OAuth/resetOfficialPassword/${mobile}/${smsCode}`, {
      skipAuth: true
    })
  },

  // 退出登录
  logout() {
    return request.post('/OAuth/Logout')
  },

  // 获取当前用户信息
  getCurrentUser() {
    return request.get('/OAuth/me')
  },

  // 验证验证码
  verifyCaptcha(code, timestamp) {
    if (!code || !timestamp) {
      return Promise.reject(new Error('验证码和时间戳不能为空'))
    }
    return request.post('/OAuth/VerifyCaptcha', { code, timestamp }, { skipAuth: true })
  },

  // 验证 Token 是否有效
  isTokenValid() {
    const accessToken = localStorage.getItem('accessToken')
    const expiresIn = parseInt(localStorage.getItem('expiresIn') || '0')
    if (!accessToken || !expiresIn) return false
    return Date.now() < expiresIn
  },

  // 刷新 Token
  async refreshToken() {
    try {
      const response = await request.post('/OAuth/refresh-token', {}, { skipAuth: true })
      return !!(response && response.token)
    } catch (error) {
      console.warn('刷新 Token 失败:', error)
      return false
    }
  },

  // 检查 Token 状态
  async checkTokenStatus() {
    if (!this.isTokenValid()) return false
    const expiresIn = parseInt(localStorage.getItem('expiresIn') || '0')
    const remainingTime = expiresIn - Date.now()
    if (remainingTime < 5 * 60 * 1000) {
      return await this.refreshToken()
    }
    return true
  },
  // auth.js - 在现有代码后添加以下方法

  // 获取修改密码的验证码
  async getModifyPasswordCaptcha() {
    return request.get('/permission/Users/Current/getCode')
  },




  // 修改密码接口
  // 修改密码接口（加密密码，与登录一致）
  async modifyPassword(params) {
    // 参数验证
    const requiredFields = ['oldPassword', 'password', 'code', 'timestamp']
    for (const field of requiredFields) {
      if (!params[field]) {
        return Promise.reject(new Error(`参数 ${field} 不能为空`))
      }
    }

    // 验证密码长度（4-20位）
    if (params.password.length < 4 || params.password.length > 20) {
      return Promise.reject(new Error('密码长度必须为4-20位'))
    }

    // 验证码格式（4位字符）
    if (!/^[A-Za-z0-9]{4}$/.test(params.code)) {
      return Promise.reject(new Error('验证码格式不正确'))
    }

    // 调用修改密码接口，对密码进行加密（与登录加密函数相同）
    return request.post('/permission/Users/Current/Actions/ModifyPassword', {
      oldPassword: encryptPassword(params.oldPassword),
      password: encryptPassword(params.password),
      code: params.code,
      timestamp: params.timestamp
    })
  },

}



export default authApi
